import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, MessageSquare, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Inbox {
  id: string;
  name: string;
  channel_type: string;
  identifier: string;
}

interface DepartmentInbox {
  inbox_id: string;
  is_primary: boolean;
  inbox: Inbox;
}

interface Department {
  id: string;
  name: string;
  inboxes: DepartmentInbox[];
}

interface LinkInboxesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  department: Department | null;
  availableInboxes: Inbox[];
  onSuccess: () => void;
}

export function LinkInboxesDialog({ 
  open, 
  onOpenChange, 
  department,
  availableInboxes,
  onSuccess 
}: LinkInboxesDialogProps) {
  const [selectedInboxes, setSelectedInboxes] = useState<Set<string>>(new Set());
  const [primaryInbox, setPrimaryInbox] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (department && open) {
      const linkedIds = new Set(department.inboxes.map(i => i.inbox_id));
      setSelectedInboxes(linkedIds);
      
      const primary = department.inboxes.find(i => i.is_primary);
      setPrimaryInbox(primary?.inbox_id || null);
    }
  }, [department, open]);

  const handleToggleInbox = (inboxId: string) => {
    setSelectedInboxes(prev => {
      const next = new Set(prev);
      if (next.has(inboxId)) {
        next.delete(inboxId);
        // If removing the primary, clear primary selection
        if (primaryInbox === inboxId) {
          setPrimaryInbox(null);
        }
      } else {
        next.add(inboxId);
        // If this is the first selection, make it primary
        if (next.size === 1) {
          setPrimaryInbox(inboxId);
        }
      }
      return next;
    });
  };

  // Server-side: uma única chamada atômica via RPC sync_department_inboxes
  const handleSave = async () => {
    if (!department) return;

    try {
      setSaving(true);

      const inboxIds = [...selectedInboxes];
      const { error } = await supabase.rpc('sync_department_inboxes', {
        p_department_id: department.id,
        p_inbox_ids: inboxIds,
        p_primary_inbox_id: primaryInbox,
      });

      if (error) throw error;

      toast.success('Caixas de entrada vinculadas com sucesso');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error linking inboxes:', error);
      toast.error('Erro ao vincular caixas de entrada');
    } finally {
      setSaving(false);
    }
  };

  const getChannelLabel = (channelType: string) => {
    const labels: Record<string, string> = {
      whatsapp: 'WhatsApp',
      messenger: 'Messenger',
      instagram: 'Instagram',
      webchat: 'WebChat',
      telegram: 'Telegram',
    };
    return labels[channelType] || channelType;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-base">Vincular Inbox</DialogTitle>
          <DialogDescription className="text-xs">
            Selecione as inboxes que serão vinculadas ao departamento "{department?.name}".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 max-h-[400px] overflow-y-auto py-2">
          {availableInboxes.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">
              Nenhuma inbox disponível. Crie uma inbox primeiro.
            </p>
          ) : (
            <>
              {availableInboxes.map(inbox => (
                <div 
                  key={inbox.id}
                  className={`flex items-center justify-between p-2.5 rounded-lg border transition-colors ${
                    selectedInboxes.has(inbox.id) ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                  }`}
                >
                  <div 
                    className="flex items-center space-x-2.5 flex-1 cursor-pointer"
                    onClick={() => handleToggleInbox(inbox.id)}
                  >
                    <Checkbox 
                      checked={selectedInboxes.has(inbox.id)}
                      onCheckedChange={() => handleToggleInbox(inbox.id)}
                    />
                    <div className="rounded-lg bg-muted p-1.5">
                      <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{inbox.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {getChannelLabel(inbox.channel_type)} • {inbox.identifier}
                      </p>
                    </div>
                  </div>
                  
                  {selectedInboxes.has(inbox.id) && selectedInboxes.size > 1 && (
                    <Button
                      variant={primaryInbox === inbox.id ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setPrimaryInbox(inbox.id)}
                      className="ml-2 h-7 w-7 p-0"
                    >
                      <Star className={`h-3.5 w-3.5 ${primaryInbox === inbox.id ? 'fill-current' : ''}`} />
                    </Button>
                  )}
                </div>
              ))}

              {selectedInboxes.size > 0 && (
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs text-muted-foreground mb-2">
                    <Star className="h-3 w-3 inline mr-1" />
                    A inbox primária será usada como padrão para novas conversas.
                  </p>
                  {primaryInbox && (
                    <Badge variant="outline" className="gap-1 text-xs">
                      <Star className="h-2.5 w-2.5 fill-current" />
                      Primária: {availableInboxes.find(i => i.id === primaryInbox)?.name}
                    </Badge>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}